/**
 * useQualityDashboard — TanStack Query hook for the QA Director's
 * single-pane dashboard (drafts/53 §A). The endpoint returns the
 * 4 hero KPIs + CAPA aging buckets + the deviation dept×criticality
 * heatmap + a Top-10 attention list. Polled every 60 s — matches the
 * quality-metrics-svc cadence so a tighter loop would just hit cache.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { apiFetch } from '../../api/client';

export type KpiBand = 'green' | 'amber' | 'red';

export interface KpiCell {
  label: string;
  /** Current-period value; null if the engine could not compute. */
  value: number | null;
  /** 'percent' | 'days' | 'count'. */
  unit: 'percent' | 'days' | 'count';
  band: KpiBand;
  /** Net change vs the prior period (signed). */
  delta_pct_vs_prior_period: number;
  /** Trailing 12 buckets (week or month, depending on the period selector). */
  history: number[];
}

export interface AttentionItem {
  kind: 'CAPA' | 'DEVIATION' | 'OOS' | 'WORKFLOW';
  id: string;
  /** Short label — usually a doc / capa number. */
  label: string;
  title: string;
  age_days: number | null;
  link: string;
  criticality: 'CRITICAL' | 'MAJOR' | 'MINOR' | 'BREACH';
}

export interface QualityDashboardSnapshot {
  period: 'week' | 'month' | 'quarter' | 'year';
  computed_at_utc: string;
  kpis: {
    capa_on_time_pct: KpiCell;
    deviation_cycle_median: KpiCell;
    oos_turnaround_days: KpiCell;
    sla_breach_pct: KpiCell;
  };
  capa_aging: {
    total_open: number;
    buckets: { lt30: number; b30to60: number; b60to90: number; gt90: number };
  };
  deviation_heatmap: { dept: string; critical: number; major: number; minor: number }[];
  attention: AttentionItem[];
  signed_snapshot_available: boolean;
}

export function useQualityDashboard(): UseQueryResult<QualityDashboardSnapshot, Error> {
  return useQuery({
    queryKey: ['quality-dashboard', 'snapshot'],
    queryFn: ({ signal }) =>
      apiFetch<QualityDashboardSnapshot>('/api/v1/quality-dashboard/snapshot', { signal }),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
}
